/**
 * ReferencesSlide - Slide 2
 * Image upload with drag/drop zone and reference pool grid
 */

import { useState, useMemo } from "react";
import { ReferenceDropZone } from "../components/ReferenceDropZone";
import { ReferenceCard } from "../components/ReferenceCard";
import {
  LibraryAssetDrawer,
  type SelectedAsset,
} from "../components/LibraryAssetDrawer";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { ReferenceImage } from "../types";

const MIN_IMAGES = 5;
const IDEAL_IMAGES = 25;

interface ReferencesSlideProps {
  references: ReferenceImage[];
  onAddReferences: (files: File[]) => void | Promise<void>;
  onRemoveReference: (id: string) => void | Promise<void>;
  onToggleTriggerWord: (id: string) => void;
  /** Real-mode signal: a dataset upload is in flight. */
  realModeUploading?: boolean;
  /** Real-mode signal: the dataset asset list is loading. */
  realModeLoading?: boolean;
  /** Project ID to load library assets from */
  libraryProjectId?: string;
  /** Called when assets are selected from library */
  onAddFromLibrary?: (assets: SelectedAsset[]) => void | Promise<void>;
}

export function ReferencesSlide({
  references,
  onAddReferences,
  onRemoveReference,
  onToggleTriggerWord,
  realModeUploading,
  realModeLoading,
  libraryProjectId,
  onAddFromLibrary,
}: ReferencesSlideProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const count = references.length;
  const isBelowMinimum = count < MIN_IMAGES;
  const isAtIdeal = count >= IDEAL_IMAGES;

  // Track existing asset IDs to filter them out in the drawer
  const existingAssetIds = useMemo(
    () => new Set(references.map((r) => r.id)),
    [references],
  );

  const handleAddFromLibrary = (assets: SelectedAsset[]) => {
    if (onAddFromLibrary) {
      void onAddFromLibrary(assets);
    }
    setIsDrawerOpen(false);
  };

  return (
    <div className="flex gap-8">
      {/* Left column - Intro and dropzone */}
      <div className="w-80 flex-shrink-0">
        {/* Header */}
        <h2 className="text-2xl font-semibold text-foreground mb-3">
          Upload references
        </h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
          Upload high-quality images that represent this character. Include
          varied lighting, angles, and expressions for best results. Each image
          will be automatically captioned.
        </p>

        {/* Requirements */}
        <div className="space-y-2 mb-6">
          <Requirement
            met={count >= MIN_IMAGES}
            text={`Minimum ${MIN_IMAGES} images required`}
          />
          <Requirement met={count >= 10} text="10+ images recommended" />
          <Requirement
            met={count >= IDEAL_IMAGES}
            text={`${IDEAL_IMAGES} images for best results`}
          />
        </div>

        {/* Drop zone */}
        <ReferenceDropZone
          onFilesSelected={(files) => {
            void onAddReferences(files);
          }}
          disabled={isAtIdeal || Boolean(realModeUploading)}
        />

        {isAtIdeal && (
          <p className="text-xs text-muted-foreground mt-3">
            Maximum {IDEAL_IMAGES} images reached
          </p>
        )}
        {realModeUploading && (
          <p className="text-xs text-muted-foreground mt-3">
            Uploading to dataset project…
          </p>
        )}
        {realModeLoading && (
          <p className="text-xs text-muted-foreground mt-3">
            Loading existing references…
          </p>
        )}

        {/* Select from production assets */}
        {libraryProjectId && onAddFromLibrary && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-xs text-muted-foreground mb-2">
              Or select from your library
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsDrawerOpen(true)}
              disabled={isAtIdeal}
              className="w-full"
            >
              Browse Library
            </Button>
          </div>
        )}
      </div>

      {/* Right column - Reference pool */}
      <div className="flex-1 min-w-0 relative">
        {/* Pool header */}
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider">
            Reference Pool
          </h3>
          <CountBadge count={count} min={MIN_IMAGES} ideal={IDEAL_IMAGES} />
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-white/10 rounded-full mb-6 relative overflow-visible">
          {/* Minimum marker */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-red-500"
            style={{ left: `${(MIN_IMAGES / IDEAL_IMAGES) * 100}%` }}
          />
          {/* Ideal marker */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-green-500"
            style={{ left: "100%" }}
          />
          {/* Progress fill */}
          <div
            className={cn(
              "h-full rounded-full transition-all duration-300",
              isBelowMinimum ? "bg-red-500" : "bg-green-500",
            )}
            style={{ width: `${Math.min((count / IDEAL_IMAGES) * 100, 100)}%` }}
          />
        </div>

        {/* Reference grid */}
        {count === 0 ? (
          <div className="flex items-center justify-center h-64 rounded-lg border border-dashed border-white/10">
            <p className="text-sm text-muted-foreground">
              No references uploaded yet
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-3">
            {references.map((ref, i) => (
              <ReferenceCard
                key={ref.id}
                reference={ref}
                index={i}
                onRemove={() => {
                  void onRemoveReference(ref.id);
                }}
                onToggleTriggerWord={() => onToggleTriggerWord(ref.id)}
              />
            ))}
          </div>
        )}

        {/* Library Asset Drawer - overlays the reference pool when open */}
        {libraryProjectId && (
          <LibraryAssetDrawer
            projectId={libraryProjectId}
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onAddAssets={handleAddFromLibrary}
            existingAssetIds={existingAssetIds}
          />
        )}
      </div>
    </div>
  );
}

// Requirement line item
interface RequirementProps {
  met: boolean;
  text: string;
}

function Requirement({ met, text }: RequirementProps) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          "w-1.5 h-1.5 rounded-full",
          met ? "bg-green-500" : "bg-white/20",
        )}
      />
      <span
        className={cn(
          "text-xs",
          met ? "text-green-500" : "text-muted-foreground",
        )}
      >
        {text}
      </span>
    </div>
  );
}

// Count badge
interface CountBadgeProps {
  count: number;
  min: number;
  ideal: number;
}

function CountBadge({ count, min, ideal }: CountBadgeProps) {
  const isBelowMin = count < min;

  return (
    <span
      className={cn(
        "px-2 py-0.5 rounded text-xs font-medium",
        isBelowMin
          ? "bg-red-500/10 text-red-500"
          : "bg-green-500/10 text-green-500",
      )}
    >
      {count} / {ideal}
    </span>
  );
}
